import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Divider, 
  Card, 
  CardContent,
  IconButton,
  Checkbox,
  List,
  ListItem,
  Chip,
  Fab
} from '@mui/material';
import { 
  CalendarToday, 
  DirectionsCar, 
  Business, 
  Description, 
  Download, 
  Chat, 
  Compare, 
  Add,
  Close 
} from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import SupportButton from '../components/support/SupportButton';
import api from '../services/api';

interface ReportSummary {
  id: string;
  title: string;
  created_at: string;
  dealership_name: string;
  vehicle_info: any;
  pdf_url: string;
  file_size: number;
}

interface ReportsResponse {
  reports: ReportSummary[];
  total: number;
}

const ReportsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [isComparing, setIsComparing] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await api.get('/reports/');
        const data: ReportsResponse = response.data;
        setReports(data.reports);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getVehicleDisplay = (vehicleInfo: any) => {
    if (typeof vehicleInfo === 'string') return vehicleInfo;
    if (typeof vehicleInfo === 'object' && vehicleInfo) {
      const year = vehicleInfo.year || '';
      const make = vehicleInfo.make || '';
      const model = vehicleInfo.model || '';
      return `${year} ${make} ${model}`.trim() || 'Vehicle Info';
    }
    return 'Vehicle Info';
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const handleViewReport = (reportId: string) => {
    navigate(`/reports/${reportId}`);
  };

  const handleDownloadPDF = (pdfUrl: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    window.open(pdfUrl, '_blank');
  };

  const handleCheckboxChange = (reportId: string, checked: boolean) => {
    const newSelected = new Set(selectedReports);
    if (checked) {
      if (newSelected.size < 2) {
        newSelected.add(reportId);
      }
    } else {
      newSelected.delete(reportId);
    }
    setSelectedReports(newSelected);
  };

  const handleCompare = async () => {
    if (selectedReports.size !== 2) return;
    
    setIsComparing(true);
    try {
      const reportIds = Array.from(selectedReports);
      
      const response = await api.post('/chat/compare', {
        report_ids: reportIds
      });

      const chatId = response.data.id;
      
      if (!chatId) {
        throw new Error('No chat ID received from API');
      }
      
      navigate(`/streaming-chat/${chatId}`);

    } catch (err: any) {
      console.error('Failed to create comparison chat:', err);
      
      if (err.response?.status === 401) {
        alert('Please log in again to create a comparison.');
        navigate('/login');
        return;
      }
      
      alert('Failed to create comparison chat. Please try again.');
    } finally {
      setIsComparing(false);
    }
  };

  const clearSelection = () => {
    setSelectedReports(new Set());
  };

  const getSelectedReportsInfo = () => {
    return Array.from(selectedReports).map(id => 
      reports.find(report => report.id === id)
    ).filter(Boolean);
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Loading your reports...
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button onClick={() => window.location.reload()} variant="outlined">
              Try Again
            </Button>
          </Paper>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Grid container spacing={4}>
          {/* Main content - left side */}
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 8' } }}>
            <Typography variant="h4" gutterBottom>
              My Car Reports
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {reports.length} report{reports.length !== 1 ? 's' : ''} generated
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {reports.length === 0 ? (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  textAlign: 'center', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Description sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No reports yet
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Start by analyzing a car to generate your first comprehensive report.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/chats')}>
                  Start Car Analysis
                </Button>
              </Paper>
            ) : (
              <List>
                {reports.map((report) => {
                  const isSelected = selectedReports.has(report.id);
                  
                  return (
                    <ListItem 
                      key={report.id}
                      sx={{ 
                        mb: 2,
                        p: 0,
                      }}
                    >
                      <Card 
                        sx={{ 
                          width: '100%',
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          '&:hover': {
                            borderColor: 'primary.light',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleViewReport(report.id)}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange(report.id, e.target.checked);
                              }}
                              disabled={!isSelected && selectedReports.size >= 2}
                              onClick={(e) => e.stopPropagation()}
                            />
                            
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="h6" gutterBottom>
                                {report.title}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CalendarToday sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(report.created_at)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                  {formatFileSize(report.file_size)}
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Chip 
                                  icon={<DirectionsCar />} 
                                  label={getVehicleDisplay(report.vehicle_info)}
                                  variant="outlined"
                                  size="small"
                                />
                                <Chip 
                                  icon={<Business />} 
                                  label={report.dealership_name}
                                  variant="outlined"
                                  size="small"
                                />
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                onClick={(e) => handleDownloadPDF(report.pdf_url, e)}
                                size="small"
                              >
                                <Download />
                              </IconButton>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewReport(report.id);
                                }}
                                size="small"
                                color="primary"
                              >
                                <Chat />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Grid>

          {/* Right panel - sidebar */}
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
            <Typography variant="h5" gutterBottom>
              Quick Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Analysis Tools
              </Typography>
              <Button 
                variant="contained" 
                fullWidth 
                onClick={() => navigate('/chats')}
                startIcon={<Add />}
                sx={{ mb: 2 }}
              >
                New Car Analysis
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={() => navigate('/chats')}
                startIcon={<Chat />}
              >
                View All Chats
              </Button>
            </Paper>

            {/* Selection info */}
            {selectedReports.size > 0 && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Report Comparison
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedReports.size === 1 
                    ? '1 report selected. Select 1 more to compare.'
                    : selectedReports.size === 2
                      ? `Compare ${getSelectedReportsInfo().map(r => getVehicleDisplay(r?.vehicle_info)).join(' vs ')}`
                      : `${selectedReports.size} reports selected`
                  }
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {selectedReports.size === 2 && (
                    <Button
                      variant="contained"
                      onClick={handleCompare}
                      disabled={isComparing}
                      startIcon={<Compare />}
                      fullWidth
                    >
                      {isComparing ? 'Creating...' : 'Compare'}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={clearSelection}
                    startIcon={<Close />}
                    size="small"
                  >
                    Clear
                  </Button>
                </Box>
              </Paper>
            )}

            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Reports Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Reports: {reports.length}
              </Typography>
              {reports.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Latest: {formatDate(reports[0]?.created_at)}
                </Typography>
              )}
            </Paper>

            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Need Help?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Having issues with your reports? Get support from our team.
              </Typography>
              <SupportButton
                context="report"
                variant="contained"
                fullWidth
                label="Get Support"
                showIcon={true}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Floating compare button for mobile */}
        {selectedReports.size === 2 && (
          <Fab
            color="primary"
            onClick={handleCompare}
            disabled={isComparing}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              display: { xs: 'flex', md: 'none' }
            }}
          >
            <Compare />
          </Fab>
        )}
      </Box>
    </AppLayout>
  );
};

export default ReportsListPage;